// lib
import React, { useState } from "react";
import PropTypes from "prop-types";
import { languages } from "i18n/proxy";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar as LibAppBar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import EnterIcon from "@material-ui/icons/ExitToApp";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
// src
import Drawer from "components/plannings/partials/Drawer";

const getLocaleText = (l) => (l === "fr" ? "Français" : "English");
const getLocaleIcon = (l) => <i className={`flag flag-${l}`} />;

const AppBar = ({
  // data props
  identity,
  isLight,
  isHydrated,
  isLarge,
  isLogged,
  isXSmall,
  language,
  // action props
  loginAndKeepPath,
  setLanguage,
}) => {
  // Drawer behavior
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  //
  const history = useHistory();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { t } = useTranslation();

  const handleLogin = () => loginAndKeepPath(location.pathname);

  const handleLogout = () => history.push("/logout");

  const handleOpenLanguageMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelectLanguage = (event, l) => {
    setLanguage(l);
    setAnchorEl(null);
  };

  const handleCloseLanguageMenu = () => {
    setAnchorEl(null);
  };

  const languageMenu = (
    <>
      <Button className="language-selector" onClick={handleOpenLanguageMenu} color="inherit">
        {getLocaleIcon(language)}
        <ArrowDropDown />
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseLanguageMenu}
      >
        {languages.map((l) => (
          <MenuItem
            key={l}
            selected={l === language}
            onClick={(event) => handleSelectLanguage(event, l)}
          >
            <span className="locale">
              {getLocaleIcon(l)}
              {getLocaleText(l)}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  const logoutButton = (
    <div className="user-action">
      {!isXSmall && (
        <div className="action disabled">
          <span className="first-name">{identity.firstName}</span>
          &nbsp;
          <span className="last-name">{identity.lastName}</span>
        </div>
      )}
      <IconButton edge="end" color="inherit" aria-label={t("actions.login")} onClick={handleLogout}>
        <PowerSettingsNewIcon />
      </IconButton>
      {languageMenu}
    </div>
  );
  const loginButton = (
    <div className="user-action">
      {!isXSmall && (
        <div className="action" role="button" onClick={handleLogin}>
          {t("actions.login")}
        </div>
      )}
      <IconButton aria-label={t("actions.login")} onClick={handleLogin}>
        <EnterIcon />
      </IconButton>
      {languageMenu}
    </div>
  );

  if (!isHydrated) {
    return (
      <LibAppBar className="app-appbar" color="transparent" position="fixed">
        <Toolbar className="toolbar">
          <div className="toolbar-left">
            <div className="title">
              <span className="app-logo">
                <span className="v1">M</span>
                <span>y</span>
                <span className="v2">S</span>
                <span>chool •&nbsp;</span>
              </span>
              <span className="app-logo">Plannings</span>
            </div>
          </div>
        </Toolbar>
      </LibAppBar>
    );
  }

  return (
    <>
      <LibAppBar className="app-appbar" color="transparent" position="fixed">
        <Toolbar className="toolbar">
          <div className="toolbar-left">
            {!isLight && !isLarge && (
              <IconButton onClick={handleOpen} edge="start" color="inherit" aria-label="menu">
                <MenuIcon />
              </IconButton>
            )}
            <div className="title">
              <span className="app-logo">
                <a href="/">
                  <span className="v1">M</span>
                  <span>y</span>
                  <span className="v2">S</span>
                  <span>chool</span>
                </a>
                <span>&nbsp;•&nbsp;</span>
              </span>
              <span className="app-logo">Plannings</span>
            </div>
          </div>
          {isLogged && !isLight ? logoutButton : loginButton}
        </Toolbar>
      </LibAppBar>
      {!isLarge && <Drawer open={open} onClose={handleClose} />}
    </>
  );
};

AppBar.propTypes = {
  // data props
  identity: PropTypes.object,
  isLight: PropTypes.bool.isRequired,
  isHydrated: PropTypes.bool.isRequired,
  isLarge: PropTypes.bool.isRequired,
  isLogged: PropTypes.bool.isRequired,
  isXSmall: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  // action props
  loginAndKeepPath: PropTypes.func.isRequired,
  setLanguage: PropTypes.func.isRequired,
};

AppBar.defaultProps = {
  identity: { id: "", firstName: "", lastName: "" },
};

export default AppBar;
